import { useEffect, useState } from 'react';

const useMicLevel = ({ muted, deviceId }) => {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    let rafId = 0;
    let stream;
    let ctx;
    let cancelled = false;

    if (muted) {
      setLevel(0);
      return () => {};
    }

    const run = async () => {
      try {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
          setLevel(0);
          return;
        }

        const audioConstraints = deviceId
          ? { deviceId: { exact: deviceId } }
          : true;

        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        ctx = new (window.AudioContext || window.webkitAudioContext)();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);

        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (cancelled) {
            return;
          }
          analyser.getByteFrequencyData(data);
          const avg = data.reduce((acc, v) => acc + v, 0) / data.length;
          setLevel(Math.min(100, avg * 2.5));
          rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
      } catch (_error) {
        setLevel(0);
      }
    };

    run();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (stream) stream.getTracks().forEach((track) => track.stop());
      if (ctx && typeof ctx.close === 'function') {
        ctx.close();
      }
    };
  }, [muted, deviceId]);

  return level;
};

export default useMicLevel;
